import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp } from 'lucide-react';
import type { ChatMessage as ChatMessageType } from '../../types/chatbot';

interface ChatMessageProps {
  message: ChatMessageType;
  onFeedback?: (messageId: string, helpful: boolean) => void;
}

export default function ChatMessage({ message, onFeedback }: ChatMessageProps) {
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [showRelated, setShowRelated] = useState(false);

  const handleFeedback = (helpful: boolean) => {
    if (!feedbackGiven && onFeedback) {
      onFeedback(message.id, helpful);
      setFeedbackGiven(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[80%] rounded-lg p-3 ${
          message.type === 'user'
            ? 'bg-mint-600 text-white'
            : 'bg-gray-100 text-gray-900'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>

        {message.confidence && message.type === 'bot' && (
          <div className="mt-1 text-xs text-gray-500">
            Confidence: {Math.round(message.confidence * 100)}%
          </div>
        )}

        {message.links && message.links.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.links.map((link, index) => (
              <a
                key={`${message.id}-link-${index}`}
                href={link.url}
                className={`inline-flex items-center gap-1 text-sm ${
                  message.type === 'user'
                    ? 'text-mint-100 hover:text-white'
                    : 'text-mint-600 hover:text-mint-700'
                }`}
              >
                {link.text}
                <MessageSquare className="w-4 h-4" />
              </a>
            ))}
          </div>
        )}

        {message.callToAction && (
          <div className="mt-2">
            <a
              href={`tel:${message.callToAction.number}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                message.type === 'user'
                  ? 'bg-white text-mint-600 hover:bg-mint-50'
                  : 'bg-mint-600 text-white hover:bg-mint-700'
              }`}
            >
              {message.callToAction.text}
            </a>
          </div>
        )}

        {message.type === 'bot' && message.relatedQuestions && message.relatedQuestions.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowRelated(!showRelated)}
              className="flex items-center gap-1 text-sm text-mint-600 hover:text-mint-700"
            >
              {showRelated ? 'Hide related questions' : 'Show related questions'}
              {showRelated ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {showRelated && (
              <div className="mt-2 space-y-2">
                {message.relatedQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="block w-full text-left text-sm text-gray-600 hover:text-mint-600 hover:bg-mint-50 rounded-lg p-2 transition-colors"
                  >
                    {question.question}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {message.type === 'bot' && !feedbackGiven && (
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => handleFeedback(true)}
              className="p-1 text-gray-400 hover:text-mint-600 transition-colors"
              aria-label="Helpful"
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleFeedback(false)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              aria-label="Not helpful"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>
        )}

        <p className="text-xs mt-1 opacity-70">
          {message.timestamp.toLocaleTimeString([], { 
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>
    </motion.div>
  );
}
